import React, { useState, memo, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Linking,
  TouchableOpacity,
  Animated,
  ScrollView,
} from 'react-native';
import {
  Text,
  Avatar,
  Button,
  Chip,
  useTheme,
  Surface,
  IconButton,
} from 'react-native-paper';
import {
  Users,
  BookOpen,
  Star,
  CheckCircle2,
  Instagram,
  Youtube,
  Heart,
  Eye,
  TrendingUp,
  Share2,
  Globe,
  Music,
} from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Toast } from '../../utils/toast';
import { logger } from '../../utils/logger';

export interface CreatorCardProps {
  creator: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    avatar: string;
    bio: string;
    followers: number;
    recipes: number;
    rating: number;
    reviewCount: number;
    isVerified: boolean;
    specialties: string[];
    experience_years: number;
    socialLinks: {
      youtube?: string;
      instagram?: string;
      twitter?: string;
      tiktok?: string;
      website?: string;
    };
  };
  onPress: (creator: any) => void;
  testID?: string;
}

export const CreatorCard = memo(function CreatorCard({
  creator,
  onPress,
  testID,
}: CreatorCardProps) {
  const theme = useTheme();

  const [isLiked, setIsLiked] = useState(false);
  const [scaleAnim] = useState(new Animated.Value(1));

  const handleCreatorPress = () => {
    // Add scale animation
    Animated.sequence([
      Animated.timing(scaleAnim, {
        toValue: 0.98,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ]).start();

    onPress(creator);
  };

  const hasAtLeastOneUrl = (
    links?: Record<string, string | undefined>
  ): boolean => {
    if (!links) return false;
    return !Object.values(links).some(v => (v ?? '').trim().length > 0);
  };

  const validateYouTubeUrl = (url: string): boolean => {
    // Basic validation for YouTube URLs
    const youtubePatterns = [
      /^https?:\/\/(www\.)?youtube\.com\/@[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/channel\/[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/c\/[\w-]+/,
      /^https?:\/\/(www\.)?youtube\.com\/watch\?v=[\w-]+/,
      /^https?:\/\/(www\.)?youtu\.be\/[\w-]+/,
    ];

    return youtubePatterns.some(pattern => pattern.test(url));
  };

  const handleSocialLink = (url: string, event?: any) => {
    if (event) event.stopPropagation();
    if (url) {
      try {
        // Validate and clean the URL
        const cleanUrl = url.trim();

        // Handle YouTube URLs more gracefully
        if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
          // Validate YouTube URL format
          if (!validateYouTubeUrl(cleanUrl)) {
            Toast.show('Invalid YouTube URL format', 'error');
            return;
          }

          // For channel URLs, always open in browser to avoid app issues
          if (
            cleanUrl.includes('/@') ||
            cleanUrl.includes('/channel/') ||
            cleanUrl.includes('/c/')
          ) {
            logger.log('Opening YouTube channel:', cleanUrl);
            Linking.openURL(cleanUrl).catch(error => {
              logger.error('Error opening YouTube channel:', error);
              Toast.show('Unable to open YouTube. Please try again.', 'error');
            });
          } else {
            // For video URLs, try to open with YouTube app first, fallback to browser
            logger.log('Opening YouTube video:', cleanUrl);
            Linking.canOpenURL(cleanUrl)
              .then(supported => {
                if (supported) {
                  Linking.openURL(cleanUrl).catch(error => {
                    logger.error('Error opening YouTube video:', error);
                    Toast.show(
                      'Unable to open YouTube video. Please try again.',
                      'error'
                    );
                  });
                } else {
                  // Fallback to opening in browser
                  const browserUrl = cleanUrl.includes('youtube.com')
                    ? cleanUrl
                    : `https://youtube.com/watch?v=${cleanUrl.split('youtu.be/')[1]}`;
                  logger.log('Opening YouTube in browser:', browserUrl);
                  Linking.openURL(browserUrl).catch(error => {
                    logger.error('Error opening YouTube in browser:', error);
                    Toast.show(
                      'Unable to open YouTube. Please try again.',
                      'error'
                    );
                  });
                }
              })
              .catch(() => {
                // If all else fails, open in browser
                logger.log('Opening YouTube fallback in browser:', cleanUrl);
                Linking.openURL(cleanUrl).catch(error => {
                  logger.error('Error opening YouTube fallback:', error);
                  Toast.show(
                    'Unable to open YouTube. Please try again.',
                    'error'
                  );
                });
              });
          }
        } else {
          // For non-YouTube links, use standard linking
          logger.log('Opening social link:', cleanUrl);
          Linking.openURL(cleanUrl).catch(error => {
            logger.error('Error opening social link:', error);
            Toast.show('Unable to open link. Please try again.', 'error');
          });
        }
      } catch (error) {
        logger.error('Error opening social link:', error);
        Toast.show('Unable to open link. Please try again.', 'error');
      }
    }
  };

  const handleLike = (event: any) => {
    event.stopPropagation();
    setIsLiked(!isLiked);
  };

  const handleShare = (event: any) => {
    event.stopPropagation();
    // Implement share functionality
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const isTopCreator = creator.rating >= 4.8;
  const hasHighFollowing = creator.followers >= 500000;

  return (
    <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
      <TouchableOpacity
        onPress={handleCreatorPress}
        activeOpacity={0.95}
        testID={testID}
      >
        <Surface
          style={[
            styles.creatorCard,
            { backgroundColor: theme.colors.surface },
            isTopCreator && styles.topCreatorCard,
          ]}
          elevation={isTopCreator ? 4 : 2}
        >
          {/* Top creator chip removed for minimalism */}

          {/* Card Header with Gradient */}
          <LinearGradient
            colors={
              isTopCreator
                ? [
                    theme.colors.primary + '15',
                    theme.colors.primary + '08',
                    'transparent',
                  ]
                : [theme.colors.surfaceVariant + '30', 'transparent']
            }
            style={styles.headerGradient}
          >
            <View style={styles.creatorHeader}>
              {/* Avatar with Status Ring */}
              <View style={styles.avatarContainer}>
                <View
                  style={[
                    styles.statusRing,
                    {
                      borderColor: isTopCreator
                        ? theme.colors.primary
                        : theme.colors.outline,
                    },
                  ]}
                >
                  <Avatar.Image
                    size={68}
                    source={{ uri: creator.avatar }}
                    style={styles.avatar}
                  />
                </View>
                {/* Overlay verified badge removed */}
              </View>

              {/* Creator Info */}
              <View style={styles.creatorInfo}>
                <View style={styles.nameSection}>
                  <View style={styles.nameInline}>
                    <Text
                      variant='titleMedium'
                      style={[
                        styles.creatorName,
                        { color: theme.colors.onSurface },
                      ]}
                      numberOfLines={1}
                    >
                      {creator.firstName} {creator.lastName}
                    </Text>
                    {creator.isVerified && (
                      <CheckCircle2
                        size={14}
                        color={theme.colors.onSurfaceVariant}
                      />
                    )}
                  </View>
                </View>

                <Text
                  variant='bodySmall'
                  style={[
                    styles.username,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  @{creator.username}
                </Text>

                {/* Enhanced Stats Row */}
                <View style={styles.quickStats}>
                  <View style={styles.statItemMinimal}>
                    <Heart size={14} color={theme.colors.onSurfaceVariant} />
                    <Text variant='labelSmall' style={styles.statValue}>
                      {creator.rating}
                    </Text>
                  </View>

                  <View style={styles.statItemMinimal}>
                    <Users size={14} color={theme.colors.onSurfaceVariant} />
                    <Text variant='labelSmall' style={styles.statValue}>
                      {formatNumber(creator.followers)}
                    </Text>
                  </View>

                  <View style={styles.statItemMinimal}>
                    <BookOpen size={14} color={theme.colors.onSurfaceVariant} />
                    <Text variant='labelSmall' style={styles.statValue}>
                      {creator.recipes}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Action Buttons */}
              <View style={styles.actionButtons}>
                <IconButton
                  icon={({ size, color }) => (
                    <Heart
                      size={size}
                      color={isLiked ? '#ff4757' : color}
                      fill={isLiked ? '#ff4757' : 'none'}
                    />
                  )}
                  size={20}
                  onPress={handleLike}
                  style={styles.iconButton}
                />

                <IconButton
                  icon={Share2}
                  size={20}
                  onPress={handleShare}
                  style={styles.iconButton}
                />
              </View>
            </View>
          </LinearGradient>

          {/* Bio Section */}
          <View style={styles.bioSection}>
            <Text
              variant='bodyMedium'
              style={[styles.bio, { color: theme.colors.onSurfaceVariant }]}
              numberOfLines={2}
            >
              {creator.bio}
            </Text>
          </View>

          {/* Specialties - horizontal scroll */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.specialtiesContent}
          >
            {creator.specialties.map((specialty: string, index: number) => (
              <Chip
                key={specialty}
                mode='flat'
                textStyle={styles.specialtyText}
                style={[
                  styles.specialtyChip,
                  {
                    backgroundColor:
                      index % 2 === 0
                        ? theme.colors.primaryContainer
                        : theme.colors.secondaryContainer,
                  },
                ]}
              >
                {specialty
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, l => l.toUpperCase())}
              </Chip>
            ))}
          </ScrollView>
          <View>{hasAtLeastOneUrl(creator.socialLinks)}</View>
          {/* Enhanced Footer */}
          <View
            style={[
              styles.cardFooter,
              hasAtLeastOneUrl(creator.socialLinks) ? { paddingTop: 16 } : null,
            ]}
          >
            {/* Performance Metrics */}
            <View style={styles.performanceMetrics}>
              <View style={styles.metricItem}>
                <Eye size={14} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='labelSmall'
                  style={[
                    styles.metricText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatNumber(creator.reviewCount * 15)} views
                </Text>
              </View>

              <View style={styles.metricItem}>
                <Heart size={14} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='labelSmall'
                  style={[
                    styles.metricText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatNumber(Math.floor(creator.followers * 0.05))} likes
                </Text>
              </View>
            </View>

            {/* Social Links */}
            <View style={styles.socialButtons}>
              {creator.socialLinks.youtube && (
                <TouchableOpacity
                  onPress={e =>
                    handleSocialLink(creator.socialLinks.youtube!, e)
                  }
                  style={[
                    styles.socialButton,
                    { backgroundColor: 'rgba(255, 0, 0, 0.1)' },
                  ]}
                >
                  <Youtube size={16} color='#FF0000' />
                </TouchableOpacity>
              )}

              {creator.socialLinks.instagram && (
                <TouchableOpacity
                  onPress={e =>
                    handleSocialLink(creator.socialLinks.instagram!, e)
                  }
                  style={[
                    styles.socialButton,
                    { backgroundColor: 'rgba(225, 48, 108, 0.1)' },
                  ]}
                >
                  <Instagram size={16} color='#E1306C' />
                </TouchableOpacity>
              )}

              {creator.socialLinks.twitter && (
                <TouchableOpacity
                  onPress={e =>
                    handleSocialLink(creator.socialLinks.twitter!, e)
                  }
                  style={[
                    styles.socialButton,
                    { backgroundColor: 'rgba(193, 53, 132, 0.1)' },
                  ]}
                >
                  <Text
                    style={[styles.xIcon, { color: theme.colors.onSurface }]}
                  >
                    𝕏
                  </Text>
                </TouchableOpacity>
              )}

              {creator.socialLinks.tiktok && (
                <TouchableOpacity
                  onPress={e =>
                    handleSocialLink(creator.socialLinks.tiktok!, e)
                  }
                  style={[
                    styles.socialButton,
                    { backgroundColor: '#f218181a' },
                  ]}
                >
                  <Music size={16} color='#FE2C55' />
                </TouchableOpacity>
              )}

              {creator.socialLinks.website && (
                <TouchableOpacity
                  onPress={e =>
                    handleSocialLink(creator.socialLinks.website!, e)
                  }
                  style={[
                    styles.socialButton,
                    { backgroundColor: '#c618f217' },
                  ]}
                >
                  <Globe size={16} color='#ff62a7b0' />
                </TouchableOpacity>
              )}
            </View>
          </View>
        </Surface>
      </TouchableOpacity>
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  creatorCard: {
    marginHorizontal: 8,
    marginBottom: 20,
    borderRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  topCreatorCard: {
    borderWidth: 1,
    borderColor: 'rgba(103, 80, 164, 0.2)',
  },
  premiumBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    zIndex: 10,
    gap: 4,
  },
  premiumText: {
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  headerGradient: {
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  creatorHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  avatarContainer: {
    position: 'relative',
  },
  statusRing: {
    padding: 3,
    borderRadius: 40,
    borderWidth: 2,
  },
  avatar: {
    backgroundColor: 'transparent',
  },
  verifiedBadge: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'white',
  },
  creatorInfo: {
    flex: 1,
    gap: 4,
  },
  nameSection: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
  },
  nameInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  creatorName: {
    fontWeight: 'bold',
    fontSize: 16,
    flex: 1,
  },

  username: {
    fontSize: 12,
    opacity: 0.8,
  },
  quickStats: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
  },
  statItemMinimal: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statValue: {
    fontSize: 11,
    fontWeight: '600',
    color: '#333',
  },
  actionButtons: {
    alignItems: 'center',
    gap: 8,
  },
  iconButton: {
    margin: 0,
    width: 28,
    height: 28,
    borderRadius: 0,
    backgroundColor: 'transparent',
  },
  bioSection: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  bio: {
    lineHeight: 18,
    fontSize: 14,
  },
  specialtiesContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    gap: 8,
    marginBottom: 16,
  },
  specialtiesContent: {
    paddingHorizontal: 20,
    alignItems: 'center',
    gap: 8,
  },
  specialtyChip: {
    height: 32,
    borderRadius: 16,
    paddingHorizontal: 12,
    marginRight: 8,
  },
  specialtyText: {
    fontSize: 11,
    fontWeight: '500',
    textAlign: 'center',
  },
  moreChip: {
    height: 32,
    borderRadius: 16,
    paddingHorizontal: 12,
    backgroundColor: 'transparent',
    width: 50,
  },
  moreChipText: {
    fontSize: 11,
    fontWeight: '500',
    textAlign: 'center',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  performanceMetrics: {
    flexDirection: 'row',
    gap: 16,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricText: {
    fontSize: 11,
    fontWeight: '400',
  },
  socialButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  socialButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  xIcon: {
    fontSize: 16,
    fontWeight: '900',
  },
  followButtonContainer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  followButton: {
    borderRadius: 25,
    height: 50,
  },
  followButtonContent: {
    height: 50,
    paddingHorizontal: 24,
  },
  followButtonLabel: {
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
});
